import { Link } from 'react-router';
import { api, type DeployChecklistLine } from '../../api/client';
import { Button, Card, Chip, LoadError, Loading } from '../../components/ui';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Deploy day (1.13.0, Teampace 3122, proposal 74): what a deploy still needs,
 * each line ticked from what Stitch sees itself (spending, old settings, the write fence, each
 * customer's updates, Google, the meeting recording key and recorder, stitch-archive, companies'
 * storage), with a link to where it's done.
 */
export function DeployDayCard() {
  const list = useData(() => api.deployChecklist(), [], { live: false });
  const d = list.data;
  return (
    <Card
      title="Deploy day"
      action={
        d ? (
          <span className="deploy-day-head">
            <Chip tone={d.done === d.of ? 'ok' : 'warn'}>{d.done === d.of ? 'Nothing left' : `${d.done} of ${d.of} done`}</Chip>
            <Button size="small" icon="refresh" onClick={list.reload}>
              Check again
            </Button>
          </span>
        ) : undefined
      }
    >
      {list.error ? (
        <LoadError error={list.error} retry={list.reload} />
      ) : !d ? (
        <Loading label="Checking what the deploy still needs" />
      ) : (
        <>
          <div className="card-body" style={{ paddingBottom: 0 }}>
            <span className="muted">What {d.version} still needs, each line from what Stitch sees itself. It changes nothing.</span>
          </div>
          <ul className="plain-list blocker-list deploy-day" aria-label="Deploy checklist">
            {d.lines.map((l) => (
              <li key={l.id} className="blocker-row" data-state={l.state}>
                <span className="cell-stack">
                  <span>
                    <Where line={l} />
                  </span>
                  <small className="muted">{l.said}</small>
                </span>
                <Chip tone={l.state === 'done' ? 'ok' : l.state === 'problem' ? 'crit' : 'warn'} small>
                  {l.state === 'done' ? 'Done' : l.state === 'problem' ? 'Problem' : l.counts ? `${l.counts.done} of ${l.counts.of}` : 'To do'}
                </Chip>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

/** The line's title, linking to where it's done: a card on this page by its anchor, or another page. */
function Where({ line }: { line: DeployChecklistLine }) {
  const title = <strong>{line.title}</strong>;
  const hash = line.to.startsWith('/admin/system#') ? line.to.slice('/admin/system'.length) : null;
  return hash ? <a href={hash}>{title}</a> : <Link to={line.to}>{title}</Link>;
}
